import type { ProgressState } from "./progress-state";
import { gameplayConfig, type UpgradeConfig } from "../config/gameplay-config";

export type StartingSizePurchaseStatus =
  | "insufficient-funds"
  | "invalid-state"
  | "maximum-level"
  | "purchased"
  | "stale-command";

export interface StartingSizePurchaseResult {
  readonly state: ProgressState;
  readonly status: StartingSizePurchaseStatus;
}

export interface StartingSizeUpgradeView {
  readonly cost: number | null;
  readonly currentBenefit: number;
  readonly currentLevel: number;
  readonly maximumLevel: number;
  readonly nextBenefit: number | null;
  readonly totalCoins: number;
}

export type MovementSpeedPurchaseStatus = StartingSizePurchaseStatus;
export type MovementSpeedPurchaseResult = StartingSizePurchaseResult;
export type MovementSpeedUpgradeView = StartingSizeUpgradeView;
export type FoodValuePurchaseStatus = StartingSizePurchaseStatus;
export type FoodValuePurchaseResult = StartingSizePurchaseResult;
export type FoodValueUpgradeView = StartingSizeUpgradeView;

const startingSizeConfig = (): UpgradeConfig => {
  const config = gameplayConfig.upgrades.find(
    (upgrade) => upgrade.id === "starting-size",
  );
  if (config === undefined) {
    throw new Error("Starting-size upgrade configuration is absent");
  }
  return config;
};

const movementSpeedConfig = (): UpgradeConfig => {
  const config = gameplayConfig.upgrades.find(
    (upgrade) => upgrade.id === "movement-speed",
  );
  if (config === undefined) {
    throw new Error("Movement-speed upgrade configuration is absent");
  }
  return config;
};

const foodValueConfig = (): UpgradeConfig => {
  const config = gameplayConfig.upgrades.find(
    (upgrade) => upgrade.id === "food-value",
  );
  if (config === undefined) {
    throw new Error("Food-value upgrade configuration is absent");
  }
  return config;
};

const isValidLevel = (value: number, maximumLevel: number): boolean =>
  Number.isSafeInteger(value) && value >= 0 && value <= maximumLevel;

const isValidCoins = (value: number): boolean =>
  Number.isSafeInteger(value) && value >= 0;

const requireEntry = (
  entries: readonly number[],
  index: number,
  name: string,
): number => {
  const value = entries[index];
  if (value === undefined) {
    throw new RangeError(`${name} is absent for level ${String(index)}`);
  }
  return value;
};

export const getStartingSizeBenefit = (level: number): number => {
  const config = startingSizeConfig();
  if (!isValidLevel(level, config.maxLevel)) {
    throw new RangeError("Starting-size upgrade level is invalid");
  }
  return requireEntry(config.effects, level, "Starting-size benefit");
};

export const getMovementSpeedBenefit = (level: number): number => {
  const config = movementSpeedConfig();
  if (!isValidLevel(level, config.maxLevel)) {
    throw new RangeError("Movement-speed upgrade level is invalid");
  }
  return requireEntry(config.effects, level, "Movement-speed benefit");
};

export const getFoodValueBenefit = (level: number): number => {
  const config = foodValueConfig();
  if (!isValidLevel(level, config.maxLevel)) {
    throw new RangeError("Food-value upgrade level is invalid");
  }
  return requireEntry(config.effects, level, "Food-value benefit");
};

export const getFoodValueUpgradeView = (
  state: ProgressState,
): FoodValueUpgradeView => {
  const config = foodValueConfig();
  const currentLevel = state.upgrades["food-value"];
  if (
    !isValidCoins(state.totalCoins) ||
    !isValidLevel(currentLevel, config.maxLevel)
  ) {
    throw new RangeError("Food-value upgrade state is invalid");
  }
  const maximum = currentLevel === config.maxLevel;
  return {
    cost: maximum
      ? null
      : requireEntry(config.costs, currentLevel, "Food-value cost"),
    currentBenefit: requireEntry(
      config.effects,
      currentLevel,
      "Food-value benefit",
    ),
    currentLevel,
    maximumLevel: config.maxLevel,
    nextBenefit: maximum
      ? null
      : requireEntry(
          config.effects,
          currentLevel + 1,
          "Next food-value benefit",
        ),
    totalCoins: state.totalCoins,
  };
};

export const getMovementSpeedUpgradeView = (
  state: ProgressState,
): MovementSpeedUpgradeView => {
  const config = movementSpeedConfig();
  const currentLevel = state.upgrades["movement-speed"];
  if (
    !isValidCoins(state.totalCoins) ||
    !isValidLevel(currentLevel, config.maxLevel)
  ) {
    throw new RangeError("Movement-speed upgrade state is invalid");
  }
  const maximum = currentLevel === config.maxLevel;
  return {
    cost: maximum
      ? null
      : requireEntry(config.costs, currentLevel, "Movement-speed cost"),
    currentBenefit: requireEntry(
      config.effects,
      currentLevel,
      "Movement-speed benefit",
    ),
    currentLevel,
    maximumLevel: config.maxLevel,
    nextBenefit: maximum
      ? null
      : requireEntry(
          config.effects,
          currentLevel + 1,
          "Next movement-speed benefit",
        ),
    totalCoins: state.totalCoins,
  };
};

export const getStartingSizeUpgradeView = (
  state: ProgressState,
): StartingSizeUpgradeView => {
  const config = startingSizeConfig();
  const currentLevel = state.upgrades["starting-size"];
  if (
    !isValidCoins(state.totalCoins) ||
    !isValidLevel(currentLevel, config.maxLevel)
  ) {
    throw new RangeError("Starting-size upgrade state is invalid");
  }
  const maximum = currentLevel === config.maxLevel;
  return {
    cost: maximum
      ? null
      : requireEntry(config.costs, currentLevel, "Starting-size cost"),
    currentBenefit: requireEntry(
      config.effects,
      currentLevel,
      "Starting-size benefit",
    ),
    currentLevel,
    maximumLevel: config.maxLevel,
    nextBenefit: maximum
      ? null
      : requireEntry(
          config.effects,
          currentLevel + 1,
          "Next starting-size benefit",
        ),
    totalCoins: state.totalCoins,
  };
};

export const purchaseStartingSize = (
  state: ProgressState,
  expectedLevel: number,
): StartingSizePurchaseResult => {
  const config = startingSizeConfig();
  const currentLevel = state.upgrades["starting-size"];
  if (
    !isValidCoins(state.totalCoins) ||
    !isValidLevel(currentLevel, config.maxLevel) ||
    !isValidLevel(expectedLevel, config.maxLevel)
  ) {
    return { state, status: "invalid-state" };
  }
  if (expectedLevel !== currentLevel) {
    return { state, status: "stale-command" };
  }
  if (currentLevel === config.maxLevel) {
    return { state, status: "maximum-level" };
  }
  const cost = requireEntry(config.costs, currentLevel, "Starting-size cost");
  if (state.totalCoins < cost) {
    return { state, status: "insufficient-funds" };
  }
  return {
    state: {
      ...state,
      totalCoins: state.totalCoins - cost,
      upgrades: {
        ...state.upgrades,
        "starting-size": currentLevel + 1,
      },
    },
    status: "purchased",
  };
};

export const purchaseMovementSpeed = (
  state: ProgressState,
  expectedLevel: number,
): MovementSpeedPurchaseResult => {
  const config = movementSpeedConfig();
  const currentLevel = state.upgrades["movement-speed"];
  if (
    !isValidCoins(state.totalCoins) ||
    !isValidLevel(currentLevel, config.maxLevel) ||
    !isValidLevel(expectedLevel, config.maxLevel)
  ) {
    return { state, status: "invalid-state" };
  }
  if (expectedLevel !== currentLevel) {
    return { state, status: "stale-command" };
  }
  if (currentLevel === config.maxLevel) {
    return { state, status: "maximum-level" };
  }
  const cost = requireEntry(config.costs, currentLevel, "Movement-speed cost");
  if (state.totalCoins < cost) {
    return { state, status: "insufficient-funds" };
  }
  return {
    state: {
      ...state,
      totalCoins: state.totalCoins - cost,
      upgrades: {
        ...state.upgrades,
        "movement-speed": currentLevel + 1,
      },
    },
    status: "purchased",
  };
};

export const purchaseFoodValue = (
  state: ProgressState,
  expectedLevel: number,
): FoodValuePurchaseResult => {
  const config = foodValueConfig();
  const currentLevel = state.upgrades["food-value"];
  if (
    !isValidCoins(state.totalCoins) ||
    !isValidLevel(currentLevel, config.maxLevel) ||
    !isValidLevel(expectedLevel, config.maxLevel)
  ) {
    return { state, status: "invalid-state" };
  }
  if (expectedLevel !== currentLevel) {
    return { state, status: "stale-command" };
  }
  if (currentLevel === config.maxLevel) {
    return { state, status: "maximum-level" };
  }
  const cost = requireEntry(config.costs, currentLevel, "Food-value cost");
  if (state.totalCoins < cost) {
    return { state, status: "insufficient-funds" };
  }
  return {
    state: {
      ...state,
      totalCoins: state.totalCoins - cost,
      upgrades: {
        ...state.upgrades,
        "food-value": currentLevel + 1,
      },
    },
    status: "purchased",
  };
};
